/**
 * Constructor browse facets — same CSS surface as Shopify native (component-facets.css).
 */
class ConstructorFacets extends HTMLElement {
  static #TRUTHY = new Set(['true', '1', 'yes']);
  static #FALSY = new Set(['false', '0', 'no']);

  constructor() {
    super();
    this._selected = Object.create(null);
    this._lastFacets = [];
    this._mappingReady = false;
    this._onBrowseData = this._onBrowseData.bind(this);
    this._onDelegClick = this._onDelegClick.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onClearAll = this._onClearAll.bind(this);
    this._onBarChange = this._onBarChange.bind(this);
    this._onBarClick = this._onBarClick.bind(this);
    this._onAccordionClick = this._onAccordionClick.bind(this);
    this._refreshFadeouts = this._refreshFadeouts.bind(this);
    this._onDrawerClick = this._onDrawerClick.bind(this);
    this._onApplyDrawer = this._onApplyDrawer.bind(this);
  }

  connectedCallback() {
    this._browse = this.closest('constructor-browse') || this.closest('constructor-search');
    if (!this._browse) return;

    this._constructorDataEvent =
      this._browse.tagName === 'CONSTRUCTOR-SEARCH' ? 'constructor-search:data' : 'constructor-browse:data';
    this._constructorApplyEvent =
      this._browse.tagName === 'CONSTRUCTOR-SEARCH'
        ? 'constructor-search:apply-filters'
        : 'constructor-browse:apply-filters';

    const tplRoot = this.querySelector('.constructor-facets-templates');
    this._checkIconHtml = tplRoot?.querySelector('.js-ref-check-icon')?.innerHTML ?? '';
    this._caretHtml = tplRoot?.querySelector('.js-ref-caret-icon')?.innerHTML ?? '';
    this._arrowHtml = tplRoot?.querySelector('.js-ref-arrow-icon')?.innerHTML ?? '';
    this._chipRemoveHtml = tplRoot?.querySelector('.js-ref-chip-remove')?.innerHTML ?? '';

    this.removeAttribute('hidden');
    this._browse.addEventListener(this._constructorDataEvent, this._onBrowseData);
    this.addEventListener('change', this._onChange);
    this._browse.addEventListener('click', this._onDelegClick);
    this.addEventListener('click', this._onAccordionClick);
    window.addEventListener('resize', this._refreshFadeouts);
    this._clearTriggers = [
      ...this.querySelectorAll('[data-action="constructor-facets-clear"]'),
      ...(this._browse?.querySelectorAll('[data-action="constructor-facets-clear"]') || []),
    ];
    this._clearTriggers.forEach((el) => el.addEventListener('click', this._onClearAll));

    this.innerContainer = this.querySelector('[data-constructor-facet-groups]');
    this._countLines = [...this.querySelectorAll('.js-constructor-product-count-line')];
    const mobileCount =
      this._browse.querySelector('#constructorProductCountMobile') ||
      document.getElementById('constructorProductCountMobile');
    if (mobileCount) this._countLines.push(mobileCount);
    this._footerCountLines = [...(this._browse?.querySelectorAll('.js-constructor-footer-product-count') || [])];
    this.chipsRoot = this._browse?.querySelector('[data-constructor-chips]');
    this.activeWrap = this._browse?.querySelector('[data-constructor-active-wrap]');
    this.activeHeading = this._browse?.querySelector('[data-constructor-active-heading]');
    this._desktopCountLine = this._browse?.querySelector('#ConstructorProductCountDesktop');
    this._productCountEl = this.querySelector('[data-constructor-product-count]');

    this.barRoot = this._browse.querySelector('[data-constructor-bar]');
    this.barRoot?.addEventListener('change', this._onBarChange);
    this.barRoot?.addEventListener('click', this._onBarClick);

    this._drawerRoot = this._browse.querySelector('.mobile-facets__disclosure');
    this._drawerRoot?.addEventListener('click', this._onDrawerClick);

    this._applyBtn = this._browse.querySelector('.js-constructor-apply-drawer');
    this._applyBtn?.addEventListener('click', this._onApplyDrawer);

    this._hydrateFromUrl();
  }

  disconnectedCallback() {
    if (this._constructorDataEvent) {
      this._browse?.removeEventListener(this._constructorDataEvent, this._onBrowseData);
    }
    this._browse?.removeEventListener('click', this._onDelegClick);
    this.removeEventListener('click', this._onAccordionClick);
    this.removeEventListener('change', this._onChange);
    window.removeEventListener('resize', this._refreshFadeouts);
    this._clearTriggers?.forEach((el) => el.removeEventListener('click', this._onClearAll));
    this.barRoot?.removeEventListener('change', this._onBarChange);
    this.barRoot?.removeEventListener('click', this._onBarClick);
    this._drawerRoot?.removeEventListener('click', this._onDrawerClick);
    this._applyBtn?.removeEventListener('click', this._onApplyDrawer);
  }

  _isBooleanFacet(facet) {
    const opts = facet?.options || [];
    if (opts.length === 0 || opts.length > 2) return false;
    return opts.every((o) => {
      const v = String(o.value).toLowerCase();
      return ConstructorFacets.#TRUTHY.has(v) || ConstructorFacets.#FALSY.has(v);
    });
  }

  _booleanTruthOption(facet) {
    return (facet?.options || []).find((o) => ConstructorFacets.#TRUTHY.has(String(o.value).toLowerCase()));
  }

  // After data arrives, replace any URL-hydrated truthy variants ('true', 'True', '1', ...)
  // with Constructor's actual truthy option value (case-preserving). Keeps _selected canonical.
  _normalizeBooleanSelections(facets) {
    (facets || []).forEach((facet) => {
      if (!facet?.name || !this._isBooleanFacet(facet)) return;
      const set = this._selected[facet.name];
      if (!set || set.size === 0) return;
      const actual = String(this._booleanTruthOption(facet)?.value ?? '');
      if (!actual) return;
      const next = new Set();
      set.forEach((v) => {
        next.add(ConstructorFacets.#TRUTHY.has(String(v).toLowerCase()) ? actual : v);
      });
      this._selected[facet.name] = next;
    });
  }

  // Memoised once per component instance; assumes window.constructorFacetsConfig is page-load constant.
  _facetConfig(name) {
    if (!this._facetCfgMap) {
      this._facetCfgMap = Object.create(null);
      (window.constructorFacetsConfig || []).forEach((c) => {
        if (c?.name) this._facetCfgMap[c.name] = c;
      });
    }
    return this._facetCfgMap[name] || null;
  }

  // Set of every shopify_filter_param declared in constructorFacetsConfig.
  // Used by _pushUrlState to scope URL cleanup to params Constructor actually manages,
  // so non-Constructor filter.* params (other apps, campaigns, legacy URLs) are preserved.
  _managedFilterParams() {
    if (!this._managedFilterParamsSet) {
      this._managedFilterParamsSet = new Set(
        (window.constructorFacetsConfig || []).map((c) => String(c?.shopifyFilterParam || '').trim()).filter(Boolean),
      );
    }
    return this._managedFilterParamsSet;
  }

  _facetDisplayName(name) {
    return this._facetConfig(name)?.displayName || this._facetDisplayNames?.[name] || name;
  }

  _findIconEntry(rawValue) {
    const map = window.constructorFiltersMapping;
    if (!map) return null;
    const key = String(rawValue);
    if (map[key]) return map[key];

    // Constructor often returns metaobject refs as `gid://shopify/Metaobject/40196`
    // or the bare numeric id; try the trailing digits.
    const trailing = key.match(/(\d+)\s*$/);
    if (trailing && map[trailing[1]]) return map[trailing[1]];

    // Last resort: case-insensitive label match.
    const lc = key.toLowerCase();
    for (const code in map) {
      if (map[code]?.label && String(map[code].label).toLowerCase() === lc) return map[code];
    }
    return null;
  }

  _resolveOptionLabel(rawValue, displayFromApi) {
    const entry = this._findIconEntry(rawValue);
    if (entry?.label) return String(entry.label);
    if (displayFromApi != null && String(displayFromApi).trim() !== '') {
      return String(displayFromApi);
    }
    return String(rawValue);
  }

  _optionLabel(name, rawValue, displayFromApi) {
    const override = this._facetConfig(name)?.optionLabels?.[rawValue];
    if (override) return override;
    return this._resolveOptionLabel(rawValue, displayFromApi);
  }

  _optionIconHtml(rawValue) {
    const entry = this._findIconEntry(rawValue);
    if (!entry) return '';
    const bg = entry.image ? `url('${entry.image}')` : entry.color || '';
    if (!bg) return '';
    const isWhite = !entry.image && /^#fff(fff)?$/i.test(entry.color || '');
    const stroke = isWhite ? '#52595B' : '#fff';
    return `<div class="facets__item_label_icon" style="background:${bg};background-size:contain;">
      <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-checkmark-metafield" width="28" height="20" fill="none" aria-hidden="true">
        <path stroke="${stroke}" stroke-linecap="round" stroke-width="4" d="M2.5 8c4 2.6 9.19 10.4 9.79 8.8C13.59 13.3 19.48 7 25.47 2"/>
      </svg>
    </div>`;
  }

  _isPillFacet(facetName) {
    if (this._facetConfig(facetName)?.displayType === 'pill') return true;
    if (!this._pillFacetSet) {
      const raw = window.constructorPillFacets || '';
      this._pillFacetSet = new Set(
        raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      );
    }
    return this._pillFacetSet.has(facetName);
  }

  _facetKind(facet) {
    const type = this._facetConfig(facet?.name)?.displayType;
    if (type === 'toggle' || this._isBooleanFacet(facet)) return 'boolean';
    if (type === 'pill') return 'list';
    const opts = facet?.options || [];
    const hasIcon = opts.some((o) => {
      const e = this._findIconEntry(String(o.value));
      return e && (e.image || e.color);
    });
    return hasIcon ? 'metafield' : 'list';
  }

  _onDrawerClick(event) {
    const target = event.target;
    if (!target) return;

    const submenuTrigger = target.closest?.('.mobile-facets__close-button, .mobile-facets__back-button');
    if (submenuTrigger) {
      const details = submenuTrigger.closest('details.mobile-facets__details');
      if (details) {
        event.preventDefault();
        this._closeSubmenu(details);
        return;
      }
    }

    const isCloseTrigger =
      target.closest?.('.js-details-close') ||
      target.classList?.contains('mobile-facets') ||
      target.classList?.contains('mobile_sort_container');
    if (!isCloseTrigger) return;
    this._closeDrawer();
  }

  _closeSubmenu(details) {
    details.classList.remove('menu-opening');
    const summary = details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    setTimeout(() => details.removeAttribute('open'), ConstructorBase.DRAWER_CLOSE_MS);
  }

  _onApplyDrawer(event) {
    event.preventDefault();
    this._broadcastFilters();
    this._closeDrawer();
  }

  _closeDrawer() {
    this._drawerRoot?.removeAttribute('open');
    this._drawerRoot?.classList.remove('menu-opening');
    document.body.classList.remove('overflow-hidden', 'overflow-hidden-mobile');
  }

  async _onBrowseData(event) {
    const response = event.detail?.response;
    const facets = Array.isArray(response?.facets) ? response.facets : [];
    this._lastFacets = facets;
    this._normalizeBooleanSelections(facets);
    this._facetDisplayNames = facets.reduce((acc, f) => {
      if (f?.name) acc[f.name] = f.display_name || f.name;
      return acc;
    }, {});
    const total = typeof response?.total_num_results === 'number' ? response.total_num_results : null;
    if (total !== null) this._syncProductCounts(total);

    if (window.constructorFiltersMappingReady) {
      try {
        await window.constructorFiltersMappingReady;
      } catch (_) {
        // proceed with whatever we have
      }
    }
    // Gate first chip render until the metaobject mapping is resolved, otherwise
    // _syncActiveChipsUi (called from _syncProductCounts above) flashes raw IDs on
    // cold-cache loads when Constructor returns no options for active filters.
    this._mappingReady = true;

    this._optionDisplayNames = facets.reduce((acc, f) => {
      if (!f?.name) return acc;
      const map = (acc[f.name] = {});
      (f.options || []).forEach((o) => {
        if (o?.value != null) {
          map[String(o.value)] = this._resolveOptionLabel(String(o.value), o.display_name);
        }
      });
      return acc;
    }, {});

    const ordered = this._orderedFacets(facets);
    this._renderBar(ordered);

    const container = this.innerContainer;
    if (!container) return;

    container.innerHTML = ordered.map((facet, idx) => this._facetBlockHtml(facet, idx)).join('');

    if (container.childElementCount) {
      this.classList.remove('constructor-facets--empty');
    } else {
      this.classList.add('constructor-facets--empty');
    }

    container.querySelectorAll('input[data-facet-name]').forEach((cb) => {
      const name = cb.dataset.facetName;
      const val = cb.dataset.value;
      const set = this._selected[name];
      cb.checked = Boolean(set && set.has(val));
    });

    this._observeFadeouts();
    this._syncActiveChipsUi();
  }

  _onAccordionClick(event) {
    const header = event.target.closest('.mobile_accordion_header');
    if (!header || !this.contains(header)) return;
    const preview = header.nextElementSibling;
    if (!preview?.classList.contains('mobile-facets__metafields_preview')) return;

    const list = preview.querySelector('.mobile-facets__list');
    const visible = list
      ? Array.from(list.querySelectorAll('.mobile-facets__item')).filter((item) => !item.classList.contains('hidden'))
      : [];
    const maxItems = window.innerWidth < 375 ? 4 : 5;
    if (visible.length > maxItems) preview.classList.toggle('open');
    else preview.classList.remove('open');
  }

  _observeFadeouts() {
    if (!this.innerContainer) return;
    this._fadeoutHandlers = this._fadeoutHandlers || new WeakMap();

    this.innerContainer.querySelectorAll('.mobile-facets__list').forEach((list) => {
      if (this._fadeoutHandlers.has(list)) return;
      const container = list.parentElement;
      const update = () => {
        if (!container) return;
        const overflow = list.scrollWidth > list.offsetWidth;
        if (!overflow) {
          container.classList.remove('fadeout_left', 'fadeout_right');
          return;
        }
        container.classList.toggle('fadeout_left', list.scrollLeft > 50);
        container.classList.toggle('fadeout_right', list.scrollLeft + list.offsetWidth < list.scrollWidth - 50);
      };
      update();
      list.addEventListener('scroll', update);
      this._fadeoutHandlers.set(list, update);
    });
  }

  _refreshFadeouts() {
    if (!this.innerContainer || !this._fadeoutHandlers) return;
    this.innerContainer.querySelectorAll('.mobile-facets__list').forEach((list) => {
      const handler = this._fadeoutHandlers.get(list);
      if (handler) handler();
    });
  }

  _syncProductCounts(total) {
    const strings = window.constructorStrings || {};
    const simpleTemplate =
      total === 1
        ? strings.facetProductCountTplOne || strings.facetProductCountTpl
        : strings.facetProductCountTplOther || strings.facetProductCountTpl;
    let simpleText = '';
    if (simpleTemplate && simpleTemplate.includes('__count__')) {
      simpleText = simpleTemplate.replace(/\b__count__\b/g, String(total));
    } else if (simpleTemplate) {
      simpleText = simpleTemplate;
    } else {
      simpleText = String(total);
    }
    this._countLines.forEach((el) => {
      if (!el) return;
      if (el.id === 'constructorProductCountMobile') {
        el.textContent = String(total);
      } else {
        el.textContent = simpleText;
      }
      el.hidden = false;
    });
    this._footerCountLines.forEach((el) => {
      if (!el) return;
      el.textContent = `(${total})`;
      el.classList.remove('visually-hidden');
    });
    if (this._desktopCountLine) {
      // Seed the unfiltered baseline on the first response that arrives without active filters.
      // Browse pre-fills data-total-count from Liquid (collection.all_products_count); search starts
      // at 0 and relies on this seeding so subsequent filtered responses can show "X of Y".
      const stored = parseInt(this._desktopCountLine.dataset.totalCount, 10);
      if ((!Number.isFinite(stored) || stored === 0) && Object.keys(this._selected || {}).length === 0) {
        this._desktopCountLine.dataset.totalCount = String(total);
      }
      const all = parseInt(this._desktopCountLine.dataset.totalCount, 10);
      const filtered = Number.isFinite(all) && all > 0 && total < all;
      const filteredTemplate =
        total === 1
          ? strings.facetProductCountFilteredTplOne || strings.facetProductCountFilteredTpl || ''
          : strings.facetProductCountFilteredTplOther || strings.facetProductCountFilteredTpl || '';
      const desktopText = filtered
        ? filteredTemplate.replace(/\b__count__\b/g, String(total)).replace(/\b__total__\b/g, String(all))
        : simpleText;
      this._desktopCountLine.textContent = desktopText;
      const hasActiveFilters = Object.keys(this._selected || {}).length > 0;
      this._desktopCountLine.classList.toggle('hidden', !hasActiveFilters);
    }
    this._productCountEl?.classList?.remove('hidden');
    this._syncActiveChipsUi();
  }

  _facetBlockHtml(facet, idx) {
    switch (this._facetKind(facet)) {
      case 'boolean':
        return this._renderToggleBlock(facet, idx);
      case 'metafield':
        return this._renderMetafieldBlock(facet, idx);
      default:
        return this._renderListBlock(facet, idx);
    }
  }

  _renderToggleBlock(facet, idx) {
    const tpl = document.getElementById('constructor-facets-toggle-block');
    if (!tpl) return '';
    const truthOpt = this._booleanTruthOption(facet);
    if (!truthOpt) return '';

    const facetName = facet.name;
    const sid = this.dataset.sectionId || '';
    const safeId = `Filter-constructor-drawer-${facetName}-${sid}-${idx}`.replace(/[^a-zA-Z0-9_-]/g, '');
    const truthVal = String(truthOpt.value);
    const checked = this._selected[facetName]?.has(truthVal);

    const clone = tpl.content.firstElementChild.cloneNode(true);
    clone.id = `Details-Constructor-${idx}-${sid}`;
    clone.dataset.index = `constructor-${idx}`;
    clone.dataset.facetName = facetName;

    const titleEl = clone.querySelector('[data-constructor-toggle-title]');
    if (titleEl) titleEl.textContent = this._facetDisplayName(facetName);

    const labelEl = clone.querySelector('[data-constructor-toggle-label]');
    if (labelEl) labelEl.setAttribute('for', safeId);

    const input = clone.querySelector('[data-constructor-toggle-input]');
    if (input) {
      input.id = safeId;
      input.dataset.facetName = facetName;
      input.dataset.value = truthVal;
      // outerHTML reflects attributes, not the `checked` IDL property — use the attribute.
      if (checked) input.setAttribute('checked', '');
    }

    // _facetBlockHtml joins string results, so serialize back. Browser handles escaping.
    return clone.outerHTML;
  }

  _renderMetafieldBlock(facet, idx) {
    const facetName = facet.name;
    const title = String(this._facetDisplayName(facetName)).replace(/</g, '&lt;');
    const sid = this.dataset.sectionId || '';
    const selectedN = this._selected[facetName]?.size ?? 0;
    const itemsHtml = (facet.options || [])
      .map((opt, optIdx) => {
        const value = String(opt.value);
        const entry = this._findIconEntry(value);
        const hasIcon = !!(entry && (entry.image || entry.color));
        const label = String(this._optionLabel(facetName, value, opt.display_name)).replace(/</g, '&lt;');
        const count = typeof opt.count === 'number' ? opt.count : '';
        const safeId = `Filter-constructor-${facetName}-${sid}-${idx}-${optIdx}`.replace(/[^a-zA-Z0-9_-]/g, '');
        const disabled = typeof opt.count === 'number' && opt.count <= 0;
        const hasSel = this._selected[facetName]?.has(value);
        const dimmed = disabled && !hasSel;
        const hidden = !hasIcon || dimmed;
        const iconHtml = this._optionIconHtml(value);

        return `
          <li class="mobile-facets__item list-menu__item${hidden ? ' hidden' : ''}">
            <label
              for="${safeId}"
              class="mobile-facets__label${dimmed ? ' mobile-facets__label--disabled' : ''}"
            >
              <input
                type="checkbox"
                id="${safeId}"
                class="mobile-facets__checkbox"
                data-facet-name="${facetName}"
                data-value="${value.replace(/"/g, '&quot;')}"
                ${dimmed ? 'disabled aria-disabled="true"' : ''}
              >
              ${iconHtml}
              <span aria-hidden="true">${label}<span class="mobile-facets__item_count"> (${count})</span></span>
            </label>
          </li>
        `;
      })
      .join('');

    const selectedClass = selectedN ? '' : ' hidden';
    const selectedText = selectedN ? ` (${selectedN})` : '';

    return `
      <div
        id="Details-Constructor-${idx}-${sid}"
        class="mobile_accordion mobile-facets__details_metafields js-filter constructor-facet-details"
        data-index="constructor-${idx}"
        data-facet-name="${facetName}"
      >
        <div class="mobile_accordion_header">
          <span>${title}</span>
          <span class="facets__selected${selectedClass}">${selectedText}</span>
          <span class="mobile-facets__arrow">${this._arrowHtml}</span>
        </div>
        <div class="mobile-facets__metafields_preview fadeout_right">
          <ul class="mobile-facets__list list-unstyled" role="list">
            ${itemsHtml || `<li><p class="caption">${window.constructorStrings?.facetNoValues || ''}</p></li>`}
          </ul>
        </div>
      </div>
    `;
  }

  _renderListBlock(facet, idx) {
    const facetName = facet.name;
    const usePills = this._isPillFacet(facetName);
    const title = String(this._facetDisplayName(facetName)).replace(/</g, '&lt;');
    const sid = this.dataset.sectionId || '';
    const selectedN = this._selected[facetName]?.size ?? 0;
    const submenuId = `FacetConstructor-${idx}-${sid}`;
    const saveLabel = String(window.constructorStrings?.facetSave || 'Save').replace(/</g, '&lt;');

    const itemsHtml = (facet.options || [])
      .map((opt, optIdx) => {
        const value = String(opt.value);
        const label = String(this._optionLabel(facetName, value, opt.display_name)).replace(/</g, '&lt;');
        const count = typeof opt.count === 'number' ? opt.count : '';
        const safeId = `Filter-constructor-${facetName}-${sid}-${idx}-${optIdx}`.replace(/[^a-zA-Z0-9_-]/g, '');
        const disabled = typeof opt.count === 'number' && opt.count <= 0;
        const hasSel = this._selected[facetName]?.has(value);
        const dimmed = disabled && !hasSel;

        if (usePills) {
          return `
            <li class="mobile-facets__item list-menu__item${dimmed ? ' hidden' : ''}">
              <label
                for="${safeId}"
                class="mobile-facets__label mobile-facets__label--pill${dimmed ? ' mobile-facets__label--disabled' : ''}"
              >
                <input
                  type="checkbox"
                  id="${safeId}"
                  class="mobile-facets__checkbox"
                  data-facet-name="${facetName}"
                  data-value="${value.replace(/"/g, '&quot;')}"
                  ${dimmed ? 'disabled aria-disabled="true"' : ''}
                >
                <div class="mobile-facets__label-text"><span>${label}</span></div>
              </label>
            </li>
          `;
        }

        return `
          <li class="mobile-facets__item list-menu__item${dimmed ? ' hidden' : ''}">
            <label
              for="${safeId}"
              class="mobile-facets__label${dimmed ? ' mobile-facets__label--disabled' : ''}"
            >
              <input
                type="checkbox"
                id="${safeId}"
                class="mobile-facets__checkbox"
                data-facet-name="${facetName}"
                data-value="${value.replace(/"/g, '&quot;')}"
                ${dimmed ? 'disabled aria-disabled="true"' : ''}
              >
              <span aria-hidden="true">${label}<span class="mobile-facets__item_count"> (${count})</span></span>
              ${this._checkIconHtml}
            </label>
          </li>
        `;
      })
      .join('');

    const itemsWrapped = usePills ? `<div class="mobile-facets__list-row">${itemsHtml}</div>` : itemsHtml;

    const selectedClass = selectedN ? '' : ' hidden';
    const selectedText = selectedN ? ` (${selectedN})` : '';
    const countBtnClass = selectedN ? '' : ' hidden';

    return `
      <details
        id="Details-Constructor-${idx}-${sid}"
        class="mobile-facets__details js-filter constructor-facet-details constructor-facet-list${usePills ? ' constructor-facet-pills' : ''}"
        data-index="constructor-${idx}"
        data-facet-name="${facetName}"
      >
        <summary class="mobile-facets__summary focus-inset">
          <div>
            <span class="facet-label">${title}</span>
            <span class="facets__selected${selectedClass}">${selectedText}</span>
            <span class="mobile-facets__arrow no-js-hidden">${this._arrowHtml}</span>
          </div>
        </summary>
        <div id="${submenuId}" class="mobile-facets__submenu gradient">
          <ul class="mobile-facets__list list-unstyled" role="list">
            <li class="mobile-facets__item list-menu__item">
              <label class="mobile-facets__label">
                <button class="mobile-facets__back-button" aria-expanded="true" type="button">
                  ${this._arrowHtml}
                  ${title}
                  <span class="active_value_count${selectedClass}">${selectedText}</span>
                </button>
              </label>
            </li>
            ${itemsWrapped}
          </ul>
          <button
            class="mobile-facets__close-button link link--text focus-inset"
            aria-expanded="true"
            type="button"
          >
            <span class="facet-back-button">${this._arrowHtml}</span>
            <div class="button button--primary">
              ${saveLabel}
              <span class="active_value_count_button${countBtnClass}">${selectedText}</span>
            </div>
          </button>
        </div>
      </details>
    `;
  }

  _orderedFacets(facets) {
    const legacyOrder = (window.constructorFacetOrder || []).map((s) => String(s).trim()).filter(Boolean);
    const rank = (name) => {
      const cfg = this._facetConfig(name);
      if (cfg && Number.isFinite(cfg.position)) return cfg.position;
      const i = legacyOrder.indexOf(name);
      return i === -1 ? Number.POSITIVE_INFINITY : i;
    };
    const visible = (facets || []).filter((facet) => facet && !facet.hidden && facet.name);
    visible.sort((a, b) => rank(a.name) - rank(b.name));
    return visible;
  }

  _renderBar(facets) {
    if (!this.barRoot) return;
    const sid = this.dataset.sectionId || '';
    const toggleTpl = document.getElementById('constructor-facets-bar-toggle');
    const openerTpl = document.getElementById('constructor-facets-bar-opener');

    const fragment = document.createDocumentFragment();

    facets.forEach((facet) => {
      const facetName = facet.name;
      const title = this._facetDisplayName(facetName);
      const safeId = `Filter-constructor-bar-${facetName}-${sid}`.replace(/[^a-zA-Z0-9_-]/g, '');

      if (this._isBooleanFacet(facet)) {
        if (!toggleTpl) return;
        const truthOpt = this._booleanTruthOption(facet);
        if (!truthOpt) return;
        const truthVal = String(truthOpt.value);
        const checked = this._selected[facetName]?.has(truthVal);

        const clone = toggleTpl.content.firstElementChild.cloneNode(true);

        const lbl = clone.querySelector('[data-constructor-bar-toggle-label]');
        if (lbl) {
          lbl.setAttribute('for', safeId);
          lbl.textContent = title;
        }

        const inputLbl = clone.querySelector('[data-constructor-bar-toggle-input-label]');
        if (inputLbl) inputLbl.setAttribute('for', safeId);

        const input = clone.querySelector('[data-constructor-bar-toggle-input]');
        if (input) {
          input.id = safeId;
          input.dataset.facetName = facetName;
          input.dataset.value = truthVal;
          if (checked) input.checked = true;
        }

        fragment.appendChild(clone);
        return;
      }

      if (!openerTpl) return;
      const selectedN = this._selected[facetName]?.size ?? 0;

      const clone = openerTpl.content.firstElementChild.cloneNode(true);
      clone.dataset.facetName = facetName;

      const titleEl = clone.querySelector('[data-constructor-bar-title]');
      if (titleEl) titleEl.textContent = title;

      const selectedEl = clone.querySelector('[data-constructor-bar-selected]');
      if (selectedEl && selectedN > 0) {
        selectedEl.textContent = ` (${selectedN})`;
        selectedEl.classList.remove('hidden');
      }

      // Append caret SVG as a direct child of the <button> so `.facets__summary > span`
      // CSS rule still only matches the facet-label, not a wrapper around the caret.
      if (this._caretHtml) {
        const tmp = document.createElement('div');
        tmp.innerHTML = this._caretHtml;
        while (tmp.firstChild) clone.appendChild(tmp.firstChild);
      }

      fragment.appendChild(clone);
    });

    this.barRoot.innerHTML = '';
    this.barRoot.appendChild(fragment);
  }

  _onBarChange(event) {
    const cb = event.target.closest('[data-bar-toggle]');
    if (!cb) return;
    const facetName = cb.dataset.facetName;
    const val = cb.dataset.value;
    if (!facetName || val === undefined) return;

    const set = this._ensureSet(facetName);
    if (cb.checked) set.add(val);
    else set.delete(val);
    if (set.size === 0) delete this._selected[facetName];

    this._broadcastFilters();
    this._syncActiveChipsUi();
  }

  _onBarClick(event) {
    const opener = event.target.closest('[data-bar-open]');
    if (!opener) return;
    event.preventDefault();

    const drawer = this._drawerRoot;
    if (!drawer) return;
    drawer.classList.add('menu-opening');
    drawer.setAttribute('open', '');
    document.body.classList.add(
      window.innerWidth < ConstructorBase.MOBILE_BODY_LOCK_PX ? 'overflow-hidden-mobile' : 'overflow-hidden',
    );

    const targetName = opener.dataset.facetName;
    if (targetName && this.innerContainer) {
      const targetBlock = this.innerContainer.querySelector(
        `.constructor-facet-details[data-facet-name="${CSS.escape(targetName)}"]`,
      );
      if (targetBlock?.matches('details.mobile-facets__details')) {
        targetBlock.setAttribute('open', '');
        requestAnimationFrame(() => {
          targetBlock.classList.add('menu-opening');
          targetBlock.querySelector('summary')?.setAttribute('aria-expanded', 'true');
        });
      } else {
        targetBlock?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }

  _ensureSet(facetName) {
    if (!this._selected[facetName]) this._selected[facetName] = new Set();
    return this._selected[facetName];
  }

  _onChange(event) {
    const cb = event.target;
    if (cb.type !== 'checkbox' || !cb.dataset.facetName) return;
    const facetName = cb.dataset.facetName;
    const val = cb.dataset.value;
    if (!facetName || val === undefined) return;

    const set = this._ensureSet(facetName);
    if (cb.checked) set.add(val);
    else set.delete(val);
    if (set.size === 0) delete this._selected[facetName];

    this._broadcastFilters();
    this._updateSummaryCounts();
    this._syncActiveChipsUi();
  }

  _updateSummaryCounts() {
    if (this.innerContainer) {
      this.innerContainer.querySelectorAll('.constructor-facet-details').forEach((block) => {
        const fname = block.dataset.facetName;
        if (!fname) return;
        const n = this._selected[fname]?.size ?? 0;
        const text = n ? ` (${n})` : '';

        block.querySelectorAll('.facets__selected, .active_value_count, .active_value_count_button').forEach((el) => {
          el.textContent = text;
          el.classList.toggle('hidden', !n);
        });
      });
    }
    if (this._lastFacets?.length) this._renderBar(this._orderedFacets(this._lastFacets));
    // Item visibility may have changed (dimmed/hidden options) — recompute fadeout overlays.
    this._refreshFadeouts();
  }

  _onDelegClick(event) {
    const removeBtn = event.target.closest('[data-constructor-chip-remove]');
    if (!removeBtn) return;
    event.preventDefault();
    const facetName = removeBtn.dataset.facetName;
    const val = removeBtn.dataset.value;
    if (!facetName || val === undefined) return;

    const set = this._selected[facetName];
    if (set) set.delete(val);
    if (!set || set.size === 0) delete this._selected[facetName];

    if (this.innerContainer) {
      this.innerContainer.querySelectorAll(`input[data-facet-name="${facetName}"]`).forEach((el) => {
        if (el.dataset.value === val) el.checked = false;
      });
    }

    this._broadcastFilters();
    this._updateSummaryCounts();
    this._syncActiveChipsUi();
  }

  _syncActiveChipsUi() {
    if (!this.chipsRoot) return;
    if (!this._mappingReady) return;

    const chipTpl = document.getElementById('constructor-facets-chip');
    const booleanFacetNames = new Set(
      (this._lastFacets || []).filter((f) => f?.name && this._isBooleanFacet(f)).map((f) => f.name),
    );

    const fragment = document.createDocumentFragment();
    let count = 0;

    Object.keys(this._selected)
      .sort()
      .forEach((fname) => {
        [...this._selected[fname]].forEach((val) => {
          if (!chipTpl) return;
          const isBooleanFacet = booleanFacetNames.has(fname);
          const cached = this._optionDisplayNames?.[fname]?.[val];
          const label = isBooleanFacet
            ? this._facetDisplayName(fname)
            : this._optionLabel(fname, String(val), cached);

          const clone = chipTpl.content.firstElementChild.cloneNode(true);

          const link = clone.querySelector('[data-constructor-chip-remove]');
          if (link) {
            link.dataset.facetName = fname;
            link.dataset.value = String(val);
          }

          const labelEl = clone.querySelector('[data-constructor-chip-label]');
          if (labelEl) labelEl.textContent = String(label);

          // Insert chip-remove icon between label and the visually-hidden hint so the
          // rendered children order matches the original (label, svg, hint).
          const inner = clone.querySelector('[data-constructor-chip-inner]');
          if (inner && this._chipRemoveHtml) {
            const visuallyHidden = inner.querySelector('.visually-hidden');
            const tmp = document.createElement('div');
            tmp.innerHTML = this._chipRemoveHtml;
            while (tmp.firstChild) inner.insertBefore(tmp.firstChild, visuallyHidden);
          }

          fragment.appendChild(clone);
          count++;
        });
      });

    this.chipsRoot.innerHTML = '';
    this.chipsRoot.appendChild(fragment);
    const any = count > 0;
    this.activeWrap?.classList.toggle('hidden', !any);
    this.activeHeading?.classList.toggle('hidden', !any);
  }

  _broadcastFilters() {
    const filters = {};
    Object.keys(this._selected).forEach((k) => {
      const values = [...this._selected[k]];
      if (values.length) filters[k] = values;
    });
    this._browse?.dispatchEvent(
      new CustomEvent(this._constructorApplyEvent || 'constructor-browse:apply-filters', {
        bubbles: false,
        detail: { filters },
      }),
    );
    this._pushUrlState();
  }

  _pushUrlState() {
    const url = new URL(window.location.href);
    const managed = this._managedFilterParams();
    [...url.searchParams.keys()].forEach((k) => {
      if (managed.has(k)) url.searchParams.delete(k);
    });
    Object.keys(this._selected).forEach((fname) => {
      const cfg = this._facetConfig(fname);
      if (!cfg?.shopifyFilterParam) return;
      const facet = (this._lastFacets || []).find((f) => f?.name === fname);
      const isToggle = cfg.displayType === 'toggle' || (facet && this._isBooleanFacet(facet));
      [...this._selected[fname]].forEach((v) => {
        const urlVal = isToggle && cfg.toggleUrlValue ? cfg.toggleUrlValue : v;
        if (urlVal != null && urlVal !== '') {
          url.searchParams.append(cfg.shopifyFilterParam, urlVal);
        }
      });
    });
    if (window.location.search !== url.search) {
      window.history.replaceState({}, '', url);
    }
  }

  // Populates `_selected` from the URL on connect. MUST NOT call _broadcastFilters /
  // _pushUrlState — the URL is already the source of truth at this stage; pushing back
  // would create a hydration loop and racy filter dispatches before data arrives.
  _hydrateFromUrl() {
    const configs = window.constructorFacetsConfig || [];
    if (!configs.length) return;
    const url = new URL(window.location.href);
    url.searchParams.forEach((urlVal, key) => {
      if (!key.startsWith('filter.')) return;
      configs
        .filter((c) => String(c.shopifyFilterParam || '').trim() === key)
        .forEach((c) => {
          const isToggle =
            String(c.displayType || '')
              .toLowerCase()
              .trim() === 'toggle';
          if (isToggle) {
            // Falls back to 'True' for facets that don't have toggle_constructor_value set yet.
            if (String(c.toggleUrlValue || '').trim() === urlVal) {
              this._ensureSet(c.name).add(String(c.toggleConstructorValue || 'True'));
            }
          } else {
            this._ensureSet(c.name).add(urlVal);
          }
        });
    });
  }

  _onClearAll(event) {
    event?.preventDefault?.();
    this._selected = Object.create(null);
    this.innerContainer?.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      cb.checked = false;
    });
    this._broadcastFilters();
    this._updateSummaryCounts();
    this._syncActiveChipsUi();
  }
}

customElements.define('constructor-facets', ConstructorFacets);
